/*
 * The bordered box at the head of the sidebar, specified once in `design/40-shell-and-pages.md`
 * "Sidebar" and built four times before this component existed (PLT-6220): the two shipped rails,
 * the prototype shell, and the `Design System/App shell` specimen that documents the slot.
 *
 * Every number here is that spec's. What the four copies actually disagreed on, which is the case
 * for having them in one place:
 *
 * - the gap between the avatar and the name: `--space__sm` in three, a literal `8px` in the
 *   corporate one (the same pixels, and no way to tell from the file that it is on the scale);
 * - the avatar's radius: `--border-radius__lg` in the specimen and the prototype, a literal `8px` in
 *   both shipped rails;
 * - the caret's ink: `--color__gray-600` in the specimen against the retiring
 *   `--platform-gray-600` in both shipped rails, which is a *different* gray (#4A5565 against
 *   #58606A, both measured in the browser) — so on the corporate rail the selector's caret and the
 *   `ActionList` section twisties beside it did not match in production, while the specimen said
 *   they did;
 * - the hover ground, on which all three answers differed: `--color__gray-50` in the specimen,
 *   Primer's `--bgColor-muted` in both shipped rails, `--color__gray-100` in the prototype;
 * - the focus ring: `--focus-ring__*` in the specimen and the prototype against a hardcoded `2px`
 *   and `--focus-outlineColor` in both shipped rails;
 * - the caret-side inset settled in PLT-6537: present in the two shipped rails, never applied to
 *   the specimen or the prototype.
 *
 * The name's leading is deliberately not in that list. It differed too — `1.4` on the corporate
 * rail, inherited on the investor one — but harmlessly, because `Text` trims the name's text box to
 * cap height and the alphabetic baseline (`text-box-trim`, see `Typography.module.css`), so what
 * line-height it inherits stops being a layout input. That trim is also what makes the descender the
 * corporate copy's `1.4` was protecting safe without it: `.truncate` clips inline and stays visible
 * in the block direction, so the "g" in "Ergo" hangs below the trimmed box rather than into a clip.
 */
.root {
  display: flex;
  align-items: center;
  gap: var(--space__sm);
  inline-size: 100%;
  box-sizing: border-box;
  border: 1px solid var(--color__gray-300);
  border-radius: var(--border-radius__md);
  /*
   * 6px, written as a literal because the space scale steps 4px to 8px and there is no token for
   * it. The spec names the number, and all four copies wrote it; a "fix" to `xs` or `sm` here
   * changes the height of the rail's first row on every shell.
   */
  padding: 6px;
  background-color: var(--color__white);
  text-align: left;
}

/*
 * The menu-trigger variant: the same box as a `<button>`, plus the three things that tell a reader
 * it opens. The static variant deliberately has none of them, because a hover ground or a focus
 * ring on a box that does not open is the same defect as the caret being there
 * (`design/10-foundations.md` "Icons": an affordance glyph doing nothing).
 */
.trigger {
  cursor: pointer;
  /*
   * The inset widens to `md` (12px) on the caret's side. A 16px stroke glyph beside a filled 36px
   * tile needs more air than the tile does to read as evenly inset, and at 6px it crowds the
   * border. Decided by Janne in PLT-6537, after the prototype's trigger measured 7px / 8px / 7px
   * across the row and still read as lopsided.
   */
  padding-inline-end: var(--space__md);
  transition: background-color 0.2s ease;
}

.trigger:hover {
  background-color: var(--color__gray-50);
}

/* Preserve the monogram variant's 36px mark + 6px padding + 1px border height. */
.text {
  min-block-size: 50px;
  padding-inline: var(--space__lg);
  border-radius: var(--border-radius__xl);
}

.trigger:focus-visible {
  outline: var(--focus-ring__width) solid var(--focus-ring__color);
  outline-offset: var(--focus-ring__offset);
}

/*
 * The identity mark. `teal-500` for every entity, which the spec states outright and gives the
 * reason for: the same organisation then looks the same everywhere, which is what a monogram is
 * for. The investor rail used to hash the entity id across teal, blue and purple, so one
 * organisation was a different colour in a customer's rail than in a screenshot of anyone else's.
 *
 * Teal here does not collide with teal's valence meaning (`design/10-foundations.md` "Colour"):
 * valence is a property of a number, and this is a letter.
 */
.avatar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  inline-size: 36px;
  block-size: 36px;
  border-radius: var(--border-radius__lg);
  background-color: var(--color__teal-500);
  color: var(--color__white);
  font-size: 18px;
  /* Leading equal to the type size: the box is square by declaration, so anything looser only
     pushes the letter off centre inside it. */
  line-height: 18px;
  font-weight: var(--font-weight__semibold);
}

/*
 * The name takes the room the avatar and the caret leave, and truncates inside it.
 *
 * `min-inline-size: 0` is what makes that work: without it this flex item's minimum is its
 * intrinsic content width, so a long organisation name pushes the box wider than the 296px rail
 * instead of ellipsising. `Text`'s own `truncate` supplies the ellipsis and the `nowrap`; the
 * `title` that lets a clipped name be read back is set in the component, as both shipped rails set
 * it before this was one.
 */
.name {
  flex: 1;
  min-inline-size: 0;
}

/* `gray-600`, the same ink as the `ActionList` section twisties in the corporate rail, so that
   rail's two chevrons match. The investor rail has no twistie to match: its menu's search
   magnifier is `gray-500` and stays there. */
.caret {
  flex: 0 0 auto;
  color: var(--color__gray-600);
}
